import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import logger from 'logger'
import { camelCase } from 'lodash'

import useSnackbar from '../Snackbar/useSnackbar'

export const PARTNER_DOCUMENTS_TYPE_MANDATE_CONTRACT = 'mandate_contract'
export const PARTNER_DOCUMENTS_TYPE_IDENTITY = 'identity'
export const PARTNER_DOCUMENTS_TYPE_HEALT_CARD = 'healt_card'
export const PARTNER_DOCUMENTS_TYPE_CHAMBER_OF_COMMERCE_CERTIFICATE =
  'chamber_of_commerce_certificate'
export const PARTNER_DOCUMENTS_TYPE_DURC = 'durc'
export const PARTNER_DOCUMENTS_TYPE_DURF = 'durf'
export const PARTNER_DOCUMENTS_TYPE_BALANCE_SHEET = 'balance_sheet'
export const PARTNER_DOCUMENTS_TYPE_ANTI_MAFIA_CERTIFICATE =
  'anti_mafia_certificate'
export const PARTNER_DOCUMENTS_TYPE_ABSENCE_OF_CONVICTION_CERTIFICATE =
  'absence_of_conviction_certificate'
export const PARTNER_DOCUMENTS_TYPE_VEHICLE_INSURANCE_POLICY =
  'vehicle_insurance_policy'
export const USER_DOCUMENTS_TYPE_OTHER_DOCUMENTS = 'other_documents'
export const USER_DOCUMENTS_TYPE_PASSPORT = 'passport'

const partnerDocumentsTypesByCountry = {
  it: [
    PARTNER_DOCUMENTS_TYPE_MANDATE_CONTRACT,
    PARTNER_DOCUMENTS_TYPE_IDENTITY,
    PARTNER_DOCUMENTS_TYPE_HEALT_CARD,
    PARTNER_DOCUMENTS_TYPE_CHAMBER_OF_COMMERCE_CERTIFICATE,
    PARTNER_DOCUMENTS_TYPE_DURC,
    PARTNER_DOCUMENTS_TYPE_DURF,
    PARTNER_DOCUMENTS_TYPE_BALANCE_SHEET,
    PARTNER_DOCUMENTS_TYPE_ANTI_MAFIA_CERTIFICATE,
    PARTNER_DOCUMENTS_TYPE_ABSENCE_OF_CONVICTION_CERTIFICATE,
    PARTNER_DOCUMENTS_TYPE_VEHICLE_INSURANCE_POLICY,
  ],
  de: [
    USER_DOCUMENTS_TYPE_PASSPORT,
    PARTNER_DOCUMENTS_TYPE_MANDATE_CONTRACT,
    PARTNER_DOCUMENTS_TYPE_BALANCE_SHEET,
    USER_DOCUMENTS_TYPE_OTHER_DOCUMENTS,
  ],
  fr: [
    USER_DOCUMENTS_TYPE_PASSPORT,
    PARTNER_DOCUMENTS_TYPE_MANDATE_CONTRACT,
    PARTNER_DOCUMENTS_TYPE_BALANCE_SHEET,
    USER_DOCUMENTS_TYPE_OTHER_DOCUMENTS,
  ],
}

export const PARTNER_DOCUMENTS_TYPES =
  partnerDocumentsTypesByCountry[config.country] || []

export const PARTNER_DOCUMENTS_TYPES_CAMEL_CASE = PARTNER_DOCUMENTS_TYPES.map(
  (type) => camelCase(type)
)

const usePartnerDocuments = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState({
    isUpdating: false,
    error: null,
  })

  const setStartUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: true,
    }))
  }, [setState])

  const setStopUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: false,
    }))
  }, [setState])

  const addDocuments = useCallback(
    async (data) => {
      setStartUpdating()

      try {
        await axios.patch(`${config.apiBaseUrl}partners/add-documents`, {
          ...data,
        })

        showSnackbarMessage({
          severity: 'success',
          message: t('partnerDocuments.upload.success', {
            attribute: t(`partnerDocuments.types.${data?.type}.title`),
          }),
        })
      } catch (e) {
        logger.error('[Error] - partner document delete', e)
        showSnackbarMessage({
          severity: 'error',
          message: t('partnerDocuments.remove.error', {
            attribute: t(`partnerDocuments.types.${data?.type}.title`),
          }),
        })
      }
      setStopUpdating()
    },
    [setStartUpdating, setStopUpdating, showSnackbarMessage, t]
  )

  const removeDocuments = useCallback(
    async (type) => {
      setStartUpdating()

      try {
        await axios.patch(`${config.apiBaseUrl}partners/remove-documents`, {
          type,
        })

        showSnackbarMessage({
          severity: 'success',
          message: t('partnerDocuments.remove.success', {
            attribute: t(`partnerDocuments.types.${type}.title`),
          }),
        })
      } catch (e) {
        logger.error('[Error] - partner document upload', e)
        showSnackbarMessage({
          severity: 'error',
          message: t('partnerDocuments.remove.error', {
            attribute: t(`partnerDocuments.types.${type}.title`),
          }),
        })
      }
      setStopUpdating()
    },
    [setStartUpdating, setStopUpdating, showSnackbarMessage, t]
  )

  return {
    isUpdating: state.isUpdating,
    addDocuments,
    removeDocuments,
  }
}

export default usePartnerDocuments
